/*
 * PRD-037 — Modal de contratación del paso de plan del claim (app SEPARADA).
 *
 * En el claim el ÚNICO método de pago ofrecido es TARJETA (la transferencia se oculta a propósito:
 * el dueño recién activado contrata en línea con tarjeta + 3DS). Por eso NO se monta el
 * PaymentMethodSelector compartido del panel — el paso de selección se salta y se va directo a
 * tarjeta. El componente compartido NO se modifica; la restricción es 100% local al claim.
 *
 * Reusa los presentacionales del panel (StripePaymentForm / isStripeConfigured) sobre
 * convertFreemiumToPlan (axiosInstance directo). Los errores se muestran inline (el partner no
 * monta <Toaster>).
 */
import { useEffect, useRef, useState } from 'react';
import { Modal } from '@plick/ui';
import { IconCreditCard, IconLock } from '@tabler/icons-react';
import { V4 } from '@components/v4/tokens';
import { Button } from '@components/v4/Button/Button';
import { StripePaymentForm, isStripeConfigured } from '@pages/Subscriptionsv2/components/StripePaymentForm';
import type { MetodoPago } from '@pages/Subscriptionsv2/hooks/useActivatePlan';
import type { AvailableSubscription } from '@interfaces/general';
import { formatToCurrency } from '@utils/formats';
import { convertFreemiumToPlan } from './useClaimPlans';

// En el claim el método de pago es siempre tarjeta (la transferencia no se ofrece).
const METODO_PAGO: MetodoPago = 'tarjeta';

interface ClaimPlanModalProps {
  plan: AvailableSubscription;
  isOpen: boolean;
  onClose: () => void;
  /** Se invoca tras una contratación exitosa (tarjeta confirmada / 3DS resuelto). */
  onActivated: () => void;
}

// 'confirm' = resumen del plan + CTA "Pagar con tarjeta" (sin selector de método);
// 'card'    = formulario de tarjeta (Stripe Elements) con el client_secret.
type Step = 'confirm' | 'card';

export const ClaimPlanModal = ({ plan, isOpen, onClose, onActivated }: ClaimPlanModalProps) => {
  const [step, setStep] = useState<Step>('confirm');
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [isActivating, setIsActivating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submittingRef = useRef(false);

  const cardConfigured = isStripeConfigured();

  // Al cambiar de plan, descartar el secret/paso del intento previo.
  useEffect(() => {
    setClientSecret(null);
    setStep('confirm');
    setError(null);
  }, [plan.id]);

  const handleClose = () => {
    setStep('confirm');
    setClientSecret(null);
    setError(null);
    onClose();
  };

  const handlePayWithCard = async () => {
    // Guard síncrono por ref: bloquea doble-click concurrente sin depender del estado async.
    if (submittingRef.current || isActivating) return;
    submittingRef.current = true;
    setError(null);
    setIsActivating(true);
    try {
      // Reusar el client_secret del intento previo en vez de duplicar la suscripción.
      if (clientSecret) {
        setStep('card');
        return;
      }

      const response = await convertFreemiumToPlan(plan.id, METODO_PAGO);

      if (response.client_secret) {
        setClientSecret(response.client_secret);
        setStep('card');
      } else {
        setError('No se recibió la información de pago. Intenta de nuevo.');
      }
    } catch (err) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (err as Error)?.message ||
        'No se pudo activar el plan. Intenta de nuevo.';
      setError(message);
    } finally {
      setIsActivating(false);
      submittingRef.current = false;
    }
  };

  return (
    <Modal padding="0rem" maxHeight="90vh" maxWidth="480px" isOpen={isOpen} onClose={handleClose}>
      <div style={{ padding: 24, fontFamily: 'Poppins, sans-serif' }}>
        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: V4.primary }}>Activar {plan.nombre}</h3>
        {typeof plan.precio === 'number' && (
          <p style={{ margin: '4px 0 20px', fontSize: 13, color: V4.grayUltraDark }}>
            {formatToCurrency(plan.precio)} · {plan.periodo_pago === 'anual' ? 'pago anual' : 'pago mensual'}
          </p>
        )}

        {step === 'confirm' && (
          <>
            {/* Método fijo = tarjeta. Sin selector (la transferencia no se ofrece en el claim). */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: 14,
                borderRadius: 10,
                border: `1.5px solid ${V4.secondary}`,
                background: '#F4FBFC',
              }}
            >
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  width: 40,
                  height: 40,
                  borderRadius: 8,
                  background: V4.secondary,
                  color: V4.white,
                }}
              >
                <IconCreditCard size={22} stroke={1.8} />
              </span>
              <div style={{ minWidth: 0 }}>
                <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: V4.primary }}>
                  Tarjeta de crédito o débito
                </p>
                <p style={{ margin: '2px 0 0', fontSize: 12, color: V4.grayUltraDark, lineHeight: 1.4 }}>
                  Cobro automático cada periodo. Pago seguro con verificación 3D Secure.
                </p>
              </div>
            </div>

            {!cardConfigured && (
              <p style={{ margin: '12px 0 0', fontSize: 12, color: V4.warningDark, lineHeight: 1.4 }}>
                El pago con tarjeta no está disponible por ahora. Vuelve a intentarlo más tarde o contacta a soporte.
              </p>
            )}

            {error && (
              <p style={{ margin: '12px 0 0', fontSize: 12, color: V4.danger, lineHeight: 1.4 }} role="alert">
                {error}
              </p>
            )}

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 24 }}>
              <Button variant="secondary" onClick={handleClose} disabled={isActivating}>
                Cancelar
              </Button>
              <Button variant="primary" onClick={handlePayWithCard} loading={isActivating} disabled={!cardConfigured}>
                Pagar con tarjeta
              </Button>
            </div>
          </>
        )}

        {step === 'card' && clientSecret && (
          <>
            <p
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                margin: '0 0 16px',
                fontSize: 12,
                color: V4.grayUltraDark,
              }}
            >
              <IconLock size={14} stroke={1.8} />
              Pago seguro procesado por Stripe.
            </p>
            <StripePaymentForm
              clientSecret={clientSecret}
              onSuccess={onActivated}
              onCancel={() => setStep('confirm')}
            />
          </>
        )}
      </div>
    </Modal>
  );
};
